import { useState } from "react";
import { motion } from "framer-motion";
import {
    Mail,
    Phone,
    MapPin,
    Instagram,
    Send,
    Sparkles,
    ChevronDown,
    ArrowUpRight,
} from "lucide-react";
import SectionReveal from "@/components/SectionReveal";
import SectionHeading from "@/components/SectionHeading";
import OrganicShapes from "@/components/OrganicShapes";
import { SEEDH } from "@/lib/seedh";
import { submitContact } from "@/lib/api";

const REASONS = [
    "Tree Plantation",
    "Food Drives",
    "Child Welfare",
    "Volunteering",
    "Donation Query",
    "General Inquiry",
];

const CONTACT_BLOCKS = [
    {
        icon: Mail,
        label: "Email us",
        value: SEEDH.email,
        linkText: "Send an email",
        href: `mailto:${SEEDH.email}`,
        tintBg: "bg-seedh-surface-green",
        accentBar: "#4A9A41",
        accentText: "text-seedh-green",
        tintBorder: "border-seedh-green/20",
        gradient: "linear-gradient(160deg, #EAF5E8 0%, #ffffff 55%)",
        fill: "#EAF5E8",
    },
    {
        icon: Phone,
        label: "Call us",
        value: SEEDH.phone,
        linkText: "Call now",
        href: `tel:${SEEDH.phoneDigits}`,
        tintBg: "bg-seedh-surface-blue",
        accentBar: "#045886",
        accentText: "text-seedh-blue",
        tintBorder: "border-seedh-blue/20",
        gradient: "linear-gradient(160deg, #E6F1F7 0%, #ffffff 55%)",
        fill: "#E6F1F7",
    },
    {
        icon: MapPin,
        label: "Visit our office",
        value: SEEDH.addressOneLine,
        linkText: "Get directions",
        href: `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(SEEDH.addressOneLine)}`,
        tintBg: "bg-seedh-surface-beige",
        accentBar: "#8B5E34",
        accentText: "text-seedh-brown",
        tintBorder: "border-seedh-brown/20",
        gradient: "linear-gradient(160deg, #F3EEE8 0%, #ffffff 55%)",
        fill: "#F3EEE8",
    },
];

export default function Contact() {
    const [form, setForm] = useState({
        name: "",
        email: "",
        phone: "",
        reason: REASONS[0],
        message: "",
    });
    const [sent, setSent] = useState(false);
    const [error, setError] = useState("");
    const [submitting, setSubmitting] = useState(false);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError("");
        setSubmitting(true);
        try {
            await submitContact({
                name: form.name,
                email: form.email,
                phone: form.phone || null,
                reason: form.reason,
                message: form.message,
            });
            setSent(true);
            setTimeout(() => setSent(false), 4500);
            setForm({
                name: "",
                email: "",
                phone: "",
                reason: REASONS[0],
                message: "",
            });
        } catch {
            setError(
                "Sorry — your message couldn't be sent. Please try again, or email us directly.",
            );
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <main
            data-testid="contact-page"
            className="bg-white pt-32 pb-24 relative overflow-hidden"
        >
            <OrganicShapes variant="blue" />
            <section className="max-w-7xl mx-auto px-6 md:px-10 relative">
                <SectionHeading
                    eyebrow="Contact us"
                    heading={["Say hello,", "ask us anything."]}
                    italicIndex={1}
                    description="Whether you want to sponsor a cause, volunteer, or simply ask how our records work — we're here. Most replies go out within 2 working days."
                />
            </section>

            {/* CONTACT BLOCKS */}
            <section className="mt-16 max-w-7xl mx-auto px-6 md:px-10 grid grid-cols-1 md:grid-cols-3 gap-5">
                {CONTACT_BLOCKS.map((b, i) => (
                    <SectionReveal delay={i * 0.08} key={b.label}>
                        <motion.a
                            href={b.href}
                            target={b.label.includes("Visit") ? "_blank" : undefined}
                            rel="noreferrer"
                            whileHover={{ y: -5, boxShadow: "0 16px 40px -16px rgba(0,0,0,0.14)" }}
                            data-testid={`contact-block-${i}`}
                            className={`group block rounded-2xl border ${b.tintBorder} p-5 shadow-sm relative overflow-hidden h-full`}
                            style={{ background: b.gradient }}
                        >
                            {/* Hover fill overlay — behind all content */}
                            <div className="absolute inset-0 z-0 opacity-0 pointer-events-none transition-opacity duration-500 group-hover:opacity-100" style={{ background: b.fill }} />
                            <div className="absolute top-0 left-0 right-0 h-[3px] z-10" style={{ background: b.accentBar }} />
                            {/* Content always above overlay */}
                            <div className="relative z-10">
                                <span className={`w-9 h-9 rounded-full ${b.tintBg} flex items-center justify-center mt-1`}>
                                    <b.icon className={`w-4 h-4 ${b.accentText}`} />
                                </span>
                                <div className="font-display text-xl text-seedh-text mt-4 leading-tight">
                                    {b.label}
                                </div>
                                <div className="text-sm text-seedh-muted mt-2 leading-relaxed">
                                    {b.value}
                                </div>
                                <div className={`mt-5 inline-flex items-center gap-1 text-xs font-semibold ${b.accentText}`}>
                                    {b.linkText}
                                    <ArrowUpRight className="w-3 h-3 transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
                                </div>
                            </div>
                        </motion.a>
                    </SectionReveal>
                ))}
            </section>

            {/* FORM + MAP */}
            <section className="mt-24 max-w-7xl mx-auto px-6 md:px-10 grid grid-cols-1 lg:grid-cols-12 gap-10">
                <SectionReveal className="lg:col-span-7">
                    <form
                        onSubmit={handleSubmit}
                        data-testid="contact-form"
                        className="group rounded-2xl border border-seedh-blue/20 shadow-sm relative overflow-hidden p-8 md:p-10"
                        style={{ background: "linear-gradient(160deg, #E6F1F7 0%, #ffffff 55%)" }}
                    >
                        <div className="absolute inset-0 z-0 opacity-0 pointer-events-none transition-opacity duration-500 group-hover:opacity-100" style={{ background: "#E6F1F7" }} />
                        <div className="absolute top-0 left-0 right-0 h-[3px] z-10" style={{ background: "#045886" }} />
                        <div className="relative z-10">
                        <span className="w-9 h-9 rounded-full bg-seedh-surface-blue flex items-center justify-center">
                            <Send className="w-4 h-4 text-seedh-blue" />
                        </span>
                        <span className="eyebrow mt-5">Send a message</span>
                        <h2 className="font-display text-3xl md:text-4xl text-seedh-blue mt-4 leading-tight">
                            We read everything that lands here.
                        </h2>

                        <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-4">
                            <Input
                                label="Name"
                                value={form.name}
                                onChange={(v) => setForm({ ...form, name: v })}
                                required
                                testId="contact-name"
                            />
                            <Input
                                label="Email"
                                type="email"
                                value={form.email}
                                onChange={(v) => setForm({ ...form, email: v })}
                                required
                                testId="contact-email"
                            />
                            <Input
                                label="Phone (optional)"
                                value={form.phone}
                                onChange={(v) => setForm({ ...form, phone: v })}
                                testId="contact-phone"
                            />
                            <SelectField
                                label="Reason"
                                value={form.reason}
                                onChange={(v) =>
                                    setForm({ ...form, reason: v })
                                }
                                options={REASONS}
                                testId="contact-reason"
                            />
                            <Input
                                label="Message"
                                value={form.message}
                                onChange={(v) =>
                                    setForm({ ...form, message: v })
                                }
                                textarea
                                required
                                testId="contact-message"
                                placeholder="Tell us a little about what you'd like to discuss…"
                                full
                            />
                        </div>

                        <button
                            type="submit"
                            disabled={submitting}
                            data-testid="contact-submit"
                            className="mt-7 btn-primary rounded-full px-7 py-4 text-sm font-semibold inline-flex items-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed"
                        >
                            <Send className="w-4 h-4" />
                            {submitting ? "Sending…" : "Submit Inquiry"}
                        </button>

                        {sent && (
                            <motion.div
                                initial={{ opacity: 0, y: 8 }}
                                animate={{ opacity: 1, y: 0 }}
                                data-testid="contact-success"
                                className="mt-6 rounded-2xl bg-seedh-surface-green border border-seedh-green/30 p-4 text-sm flex items-start gap-3"
                            >
                                <Sparkles className="w-5 h-5 text-seedh-green mt-0.5" />
                                <div>
                                    <div className="font-semibold text-seedh-text">
                                        Thanks — we'll get back to you soon.
                                    </div>
                                    <div className="text-seedh-muted text-xs mt-1">
                                        Your message has been received by the
                                        foundation team.
                                    </div>
                                </div>
                            </motion.div>
                        )}

                        {error && (
                            <motion.div
                                initial={{ opacity: 0, y: 8 }}
                                animate={{ opacity: 1, y: 0 }}
                                data-testid="contact-error"
                                className="mt-6 rounded-2xl bg-seedh-surface-beige border border-seedh-brown/30 p-4 text-sm flex items-start gap-3"
                            >
                                <div className="text-seedh-brown font-semibold">
                                    {error}
                                </div>
                            </motion.div>
                        )}
                        </div>{/* end relative z-10 */}
                    </form>
                </SectionReveal>

                <SectionReveal delay={0.15} className="lg:col-span-5">
                    <div className="group rounded-2xl border border-seedh-green/20 shadow-sm relative overflow-hidden p-6 h-full" style={{ background: "linear-gradient(160deg, #EAF5E8 0%, #ffffff 55%)" }}>
                        <div className="absolute inset-0 z-0 opacity-0 pointer-events-none transition-opacity duration-500 group-hover:opacity-100" style={{ background: "#EAF5E8" }} />
                        <div className="absolute top-0 left-0 right-0 h-[3px] z-10" style={{ background: "#4A9A41" }} />
                        <div className="relative z-10">
                        <span className="w-9 h-9 rounded-full bg-seedh-surface-green flex items-center justify-center">
                            <MapPin className="w-4 h-4 text-seedh-green" />
                        </span>
                        <span className="eyebrow mt-5">Where we work</span>
                        <h3 className="font-display text-2xl text-seedh-blue mt-4 leading-tight">
                            Based in Udaipur,
                            <br />
                            Rajasthan · India
                        </h3>
                        <p className="text-sm text-seedh-muted mt-4 leading-relaxed">
                            Office &amp; field operations across Udaipur city
                            and surrounding villages.
                        </p>
                        <p className="text-sm text-seedh-text mt-3 leading-relaxed">
                            <span className="block font-medium">{SEEDH.addressLines[0]}</span>
                            <span className="block">{SEEDH.addressLines[1]}</span>
                        </p>

                        <div className="mt-6 rounded-2xl overflow-hidden border border-seedh-border aspect-[4/3]">
                            <iframe
                                title="Seedh office — Udaipur map"
                                src="https://www.openstreetmap.org/export/embed.html?bbox=73.69%2C24.61%2C73.72%2C24.63&layer=mapnik&marker=24.6235%2C73.7042"
                                className="w-full h-full"
                                loading="lazy"
                            />
                        </div>

                        <a
                            href={SEEDH.instagram}
                            target="_blank"
                            rel="noreferrer"
                            className="mt-6 inline-flex items-center gap-2 text-sm text-seedh-blue group"
                            data-testid="contact-instagram"
                        >
                            <span className="w-9 h-9 rounded-full bg-white border border-seedh-border flex items-center justify-center group-hover:bg-seedh-surface-blue transition-colors">
                                <Instagram className="w-4 h-4" />
                            </span>
                            <span className="link-underline">
                                Follow our drives on Instagram
                            </span>
                        </a>
                        </div>{/* end relative z-10 */}
                    </div>
                </SectionReveal>
            </section>
        </main>
    );
}

function Input({
    label,
    value,
    onChange,
    type = "text",
    placeholder,
    required,
    full,
    textarea,
    testId,
}) {
    const cls = `w-full rounded-xl bg-white border border-seedh-border px-4 py-3 text-sm text-seedh-text placeholder:text-seedh-muted/60 focus:outline-none focus:border-seedh-blue focus:shadow-[0_0_0_4px_rgba(4,88,134,0.08)] transition-all ${
        textarea ? "min-h-[120px] resize-y" : ""
    }`;
    return (
        <label className={`block ${full ? "sm:col-span-2" : ""}`}>
            <span className="text-[0.65rem] tracking-[0.22em] uppercase text-seedh-brown font-semibold">
                {label}
                {required && <span className="ml-1 text-seedh-green">*</span>}
            </span>
            {textarea ? (
                <textarea
                    value={value}
                    onChange={(e) => onChange(e.target.value)}
                    placeholder={placeholder}
                    className={`mt-2 ${cls}`}
                    data-testid={testId}
                    required={required}
                />
            ) : (
                <input
                    type={type}
                    value={value}
                    onChange={(e) => onChange(e.target.value)}
                    placeholder={placeholder}
                    className={`mt-2 ${cls}`}
                    data-testid={testId}
                    required={required}
                />
            )}
        </label>
    );
}

function SelectField({ label, value, onChange, options, testId }) {
    return (
        <label className="block">
            <span className="text-[0.65rem] tracking-[0.22em] uppercase text-seedh-brown font-semibold">
                {label}
            </span>
            <div className="relative mt-2">
                <select
                    value={value}
                    onChange={(e) => onChange(e.target.value)}
                    data-testid={testId}
                    className="w-full appearance-none rounded-xl bg-white border border-seedh-border px-4 py-3 pr-10 text-sm text-seedh-text focus:outline-none focus:border-seedh-blue focus:shadow-[0_0_0_4px_rgba(4,88,134,0.08)] transition-all"
                >
                    {options.map((o) => (
                        <option key={o} value={o}>
                            {o}
                        </option>
                    ))}
                </select>
                <ChevronDown className="w-4 h-4 text-seedh-muted absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none" />
            </div>
        </label>
    );
}
